/* ════════════════════════════════════════════════════════════════════════════
 *  MAESTRO PROVIDER — the real local engine
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Talks to the Maestro (Pinokio) FastAPI server running on this machine.
 *
 * ── The API, as discovered from the local install ───────────────────────────
 *   GET    /api/v1/system-stats           health probe
 *   GET    /api/v1/models                 model list, with `is_downloaded`
 *   GET    /api/v1/resolutions            allowed "WIDTHxHEIGHT" values
 *   GET    /api/v1/defaults/{model_type}  that model's default settings
 *   POST   /api/v1/upload    (multipart)  → { filename, path, url }
 *   POST   /api/v1/generate               → { job_id, status }
 *   GET    /api/v1/status/{job_id}        → { status, progress (0-100), step,
 *                                             total_steps, phase, message,
 *                                             output_files[], error }
 *   POST   /api/v1/cancel/{job_id}
 *   GET    /api/v1/file/{filename}        the finished media file
 *
 * The server's CORS policy allows any localhost/127.0.0.1 origin, so the
 * browser can call it directly — no Vite proxy needed.
 * ════════════════════════════════════════════════════════════════════════════ */

import { GenerationError } from './types';
import type {
  AspectRatio,
  GenerationProvider,
  GenerationResult,
  Resolution,
  StartJobOptions,
} from './types';
import { dimensionsFor, getModel } from '../lib/pricing';

const env = import.meta.env ?? {};

/**
 * Where the local Maestro server lives.
 *
 * Pinokio hands Maestro a DYNAMIC port (`SERVER_PORT`); launch.py falls back to
 * 7860 and steps upward if that is taken. So rather than hard-coding it, we
 * sweep the usual ports and remember where we found it. Setting
 * VITE_MAESTRO_BASE_URL explicitly skips the search.
 */
const CONFIGURED_BASE = (env.VITE_MAESTRO_BASE_URL as string | undefined)?.replace(/\/+$/, '');
const DEFAULT_BASE = 'http://127.0.0.1:7860';

const CANDIDATE_PORTS = [7860, 7861, 7862, 7863, 7864, 7865, 7866, 7870, 8000, 8080];

let resolvedBase: string | null = CONFIGURED_BASE ?? null;

async function probe(base: string, timeoutMs = 700): Promise<boolean> {
  try {
    const res = await fetch(`${base}/api/v1/system-stats`, {
      signal: AbortSignal.timeout(timeoutMs),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** Finds (and caches) the address of the running Maestro. */
async function baseUrl(): Promise<string> {
  if (resolvedBase) return resolvedBase;
  for (const port of CANDIDATE_PORTS) {
    const candidate = `http://127.0.0.1:${port}`;
    if (await probe(candidate)) {
      resolvedBase = candidate;
      if (candidate !== DEFAULT_BASE) {
        console.info(`[Maestro] engine answered on ${candidate}`);
      }
      return candidate;
    }
  }
  // Not found — return the default so the call raises a meaningful error.
  return DEFAULT_BASE;
}

/**
 * Fallback model when the request's own choice can't be resolved at all (an
 * unknown model_type — e.g. a stale build vs. a renamed Maestro model). The
 * normal path always uses `request.settings.modelId`, mapped to a Maestro
 * model_type via the catalogue in `src/lib/pricing.ts`.
 */
const FALLBACK_MODEL: string =
  (env.VITE_MAESTRO_MODEL as string | undefined) ?? 'ti2v_2_2_fastwan';

/** Frame count for the clip. Unset means the model's own default (121 ≈ 5 s). */
const VIDEO_LENGTH_OVERRIDE = Number(env.VITE_MAESTRO_VIDEO_LENGTH) || 0;

const POLL_INTERVAL_MS = 700;
/** How long to wait patiently while a model is still downloading. */
const READY_WAIT_MS = 15 * 60 * 1000;
const READY_RETRY_MS = 4000;

const abortError = () => new DOMException('Generation cancelled.', 'AbortError');

/* ── Small helpers ────────────────────────────────────────────────────────── */

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${await baseUrl()}${path}`, init);
  if (!res.ok) {
    // FastAPI reports errors as {detail: "..."}.
    const detail = await res
      .json()
      .then((b) => (b as { detail?: string }).detail)
      .catch(() => undefined);
    throw new GenerationError(detail || `Maestro error (HTTP ${res.status}) — ${path}`);
  }
  return res.json() as Promise<T>;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(abortError());
    const id = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      window.clearTimeout(id);
      reject(abortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

interface MaestroModel {
  model_type: string;
  name: string;
  family: string;
  architecture: string;
  fps: number;
  is_downloaded?: boolean;
}

interface MaestroJobStatus {
  job_id: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress: number; // 0..100
  step: number;
  total_steps: number;
  phase: string;
  message: string;
  output_files: string[];
  error: string | null;
  oom_info?: unknown;
}

/* ── Resolution matching ─────────────────────────────────────────────────────
 * Our UI offers 480p/720p/1080p in landscape or portrait; Maestro wants a
 * concrete "WIDTHxHEIGHT" string. We pull the server's own list and pick the
 * closest option with the right orientation, so this keeps working even if an
 * update changes the available resolutions.
 * ─────────────────────────────────────────────────────────────────────────── */

let resolutionCache: string[] | null = null;

async function pickResolution(
  resolution: Resolution,
  aspect: AspectRatio,
): Promise<string> {
  const target = dimensionsFor(resolution, aspect);
  const fallback = `${target.width}x${target.height}`;
  const wantLong = Math.max(target.width, target.height);
  const portrait = aspect === 'portrait';

  try {
    if (!resolutionCache) {
      const data = await api<{ resolutions: { label: string; value: string }[] }>(
        '/api/v1/resolutions',
      );
      resolutionCache = data.resolutions.map((r) => r.value);
    }

    const options = resolutionCache
      .map((v) => {
        const [w, h] = v.split('x').map(Number);
        return { v, w, h };
      })
      .filter(
        (r) =>
          Number.isFinite(r.w) &&
          Number.isFinite(r.h) &&
          (portrait ? r.h > r.w : r.w > r.h),
      );

    if (!options.length) return fallback;
    options.sort(
      (a, b) =>
        Math.abs(Math.max(a.w, a.h) - wantLong) - Math.abs(Math.max(b.w, b.h) - wantLong),
    );
    return options[0].v;
  } catch {
    return fallback;
  }
}

/* ── Model selection ──────────────────────────────────────────────────────────
 * Each request carries its own model choice (request.settings.modelId, mapped
 * to a Maestro model_type by src/lib/pricing.ts). resolveModel checks that
 * SPECIFIC model's status rather than picking whichever happens to be
 * downloaded, so switching models in the UI actually switches what renders.
 * ─────────────────────────────────────────────────────────────────────────── */

interface ResolvedModel {
  state: 'ready' | 'downloading' | 'unknown';
  type: string;
  fps: number;
}

const modelCache = new Map<string, ResolvedModel>();

async function resolveModel(requestedType?: string): Promise<ResolvedModel> {
  const wanted = requestedType || FALLBACK_MODEL;
  const cached = modelCache.get(wanted);
  if (cached?.state === 'ready') return cached;

  const data = await api<{ models: MaestroModel[] }>('/api/v1/models');
  const match = data.models.find((m) => m.model_type === wanted);

  let resolved: ResolvedModel;
  if (match?.is_downloaded) {
    resolved = { state: 'ready', type: match.model_type, fps: match.fps || 24 };
  } else if (match) {
    // Known model, just not downloaded (yet) — the caller decides whether to
    // wait for it.
    resolved = { state: 'downloading', type: match.model_type, fps: match.fps || 24 };
  } else {
    // Unknown model_type entirely — fall back to whatever IS downloaded so
    // the app degrades instead of hard-failing on a stale/renamed id.
    const anyDownloaded = data.models.find((m) => m.is_downloaded);
    resolved = anyDownloaded
      ? { state: 'ready', type: anyDownloaded.model_type, fps: anyDownloaded.fps || 24 }
      : { state: 'unknown', type: wanted, fps: 24 };
  }

  modelCache.set(wanted, resolved);
  return resolved;
}

/* ── Reading metadata (and a poster frame) out of the downloaded clip ─────── */

interface VideoProbe {
  width: number;
  height: number;
  duration: number;
  /** JPEG data URL grabbed from inside the clip — used as the poster. */
  poster: string;
}

const EMPTY_PROBE: VideoProbe = { width: 0, height: 0, duration: 0, poster: '' };

/**
 * Reads the clip's dimensions/duration AND grabs a poster frame.
 * The Maestro API returns no thumbnail and the history view needs one, so we
 * seek into the clip and draw a frame to a canvas. The source is a same-origin
 * blob URL, so the canvas stays untainted and `toDataURL` works.
 */
function probeVideo(url: string): Promise<VideoProbe> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    let settled = false;

    const done = (r: VideoProbe) => {
      if (settled) return;
      settled = true;
      v.removeAttribute('src');
      v.load();
      resolve(r);
    };

    const grab = (): string => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx || !canvas.width) return '';
        ctx.drawImage(v, 0, 0);
        return canvas.toDataURL('image/jpeg', 0.82);
      } catch {
        return '';
      }
    };

    v.preload = 'auto';
    v.muted = true;
    v.playsInline = true;

    v.onloadeddata = () => {
      const duration = Number.isFinite(v.duration) ? v.duration : 0;
      // Seek a little way in — frame 0 of a diffusion clip is often the least
      // representative one.
      const target = duration > 0.4 ? duration * 0.35 : 0;
      v.onseeked = () =>
        done({ width: v.videoWidth, height: v.videoHeight, duration, poster: grab() });
      if (target > 0) {
        v.currentTime = target;
      } else {
        done({ width: v.videoWidth, height: v.videoHeight, duration, poster: grab() });
      }
    };

    v.onerror = () => done(EMPTY_PROBE);
    window.setTimeout(() => done(EMPTY_PROBE), 12000);
    v.src = url;
  });
}

/* ══════════════════════════════════════════════════════════════════════════ */

export const maestroProvider: GenerationProvider = {
  id: 'maestro',
  label: 'Maestro (local)',
  isLive: true,

  /**
   * Measured on this machine, 480p, model already loaded:
   *   ti2v_2_2_fastwan (5B, 3 steps)              → t2v ~170 s, i2v ~210 s
   *   ltx2_22B_distilled_gguf_q4_k_m (22B, 8 steps) → t2v ~450 s (two full
   *     denoise+decode passes — base resolution, then an upscale pass)
   * Most of the FastWan runtime is VAE decode + file write, which barely
   * depends on step count — hence the flat step weighting there. LTX's extra
   * pass makes it scale more with both resolution and steps. Rough on
   * purpose: this only feeds the pre-run hint, the live progress bar carries
   * the real numbers once a job is running.
   */
  estimateSeconds(settings) {
    const resWeight =
      settings.resolution === '1080p' ? 2.4 : settings.resolution === '480p' ? 1 : 1.6;
    const stepWeight = 0.5 + settings.steps / 60;

    if (settings.modelId === 'local-ltx2') {
      return Math.round(200 + 280 * resWeight * stepWeight);
    }
    return Math.round(70 + 110 * resWeight * stepWeight);
  },

  async healthCheck(): Promise<boolean> {
    try {
      // If the cached address went dead (e.g. Maestro restarted on another
      // port), allow rediscovery.
      if (resolvedBase && !CONFIGURED_BASE && !(await probe(resolvedBase))) {
        resolvedBase = null;
        modelCache.clear();
      }
      if (!(await probe(await baseUrl(), 3000))) return false;
      // The server is up — but it is only "ready" if at least one model is
      // downloaded (the health badge doesn't know yet which one the user will
      // pick, so any downloaded model counts here).
      const data = await api<{ models: MaestroModel[] }>('/api/v1/models').catch(() => null);
      return !!data?.models.some((m) => m.is_downloaded);
    } catch {
      return false;
    }
  },

  async generate({ request, onProgress, signal }: StartJobOptions): Promise<GenerationResult> {
    if (signal.aborted) throw abortError();

    const requestedModel = getModel(request.settings.modelId).maestroModelType;

    /* ── 0. Readiness: the chosen model may still be downloading ────────────
     * Don't throw — wait patiently and keep the UI informed. */
    const waitStart = Date.now();
    let model = await resolveModel(requestedModel);
    while (model.state === 'downloading') {
      if (Date.now() - waitStart > READY_WAIT_MS) {
        throw new GenerationError(
          `"${model.type}" is still downloading. Check the Maestro UI for progress, then try again.`,
        );
      }
      onProgress({
        stage: 'queued',
        progress: 0,
        etaSeconds: null,
        message: `Downloading ${model.type} — waiting for the engine…`,
      });
      await sleep(READY_RETRY_MS, signal);
      modelCache.delete(requestedModel || FALLBACK_MODEL);
      model = await resolveModel(requestedModel);
    }
    if (model.state === 'unknown') {
      throw new GenerationError(
        `Maestro is running but has no downloaded model at all. Open the Maestro UI and ` +
          `download one (e.g. Wan2.2 TextImage2video FastWan 5B).`,
      );
    }

    onProgress({
      stage: 'queued',
      progress: 0.02,
      etaSeconds: null,
      message: 'Preparing job',
    });

    /* ── 1. SUBMIT ──────────────────────────────────────────────────────────
     * Build on the server-side defaults and override only what the user
     * actually set, so swapping models needs no code change. */
    const defaults = await api<Record<string, unknown>>(
      `/api/v1/defaults/${encodeURIComponent(model.type)}`,
    ).catch(() => ({}) as Record<string, unknown>);

    const params: Record<string, unknown> = {
      ...defaults,
      model_type: model.type,
      prompt: request.prompt,
      resolution: await pickResolution(
        request.settings.resolution,
        request.settings.aspectRatio,
      ),
      seed: request.seed,
      generation_mode: 'video',
      image_mode: 0,
    };

    /* Step count. NOTE: FastWan is a 3-step distilled model, so our 10-50 UI
     * range would be meaningless applied literally. We map the user's choice
     * PROPORTIONALLY onto the model's own default:
     *   ui 30 (baseline) → the model's default,  ui 10 → ~1/3,  ui 50 → ~1.67x
     * The slider keeps its meaning ("more steps = finer") while the model stays
     * inside its designed operating range. */
    const modelSteps = Number(defaults.num_inference_steps) || 3;
    const scaled = Math.round(modelSteps * (request.settings.steps / 30));
    params.num_inference_steps = Math.max(1, scaled);

    if (VIDEO_LENGTH_OVERRIDE > 0) params.video_length = VIDEO_LENGTH_OVERRIDE;

    // Cache toggle: when off, ask the engine to release the model after the run.
    if (!request.settings.cacheEnabled) params.unload_model_after = true;

    /* Image-to-video: upload the still first; the server-side PATH it returns
     * goes into image_start, and image_prompt_type must carry the "S" flag. */
    if (request.mode === 'image-to-video' && request.imageDataUrl) {
      onProgress({
        stage: 'queued',
        progress: 0.05,
        etaSeconds: null,
        message: 'Uploading start image',
      });

      const blob = await (await fetch(request.imageDataUrl)).blob();
      const form = new FormData();
      form.append('file', blob, request.imageName ?? 'input.png');

      const upload = await fetch(`${await baseUrl()}/api/v1/upload`, {
        method: 'POST',
        body: form,
        signal,
      });
      if (!upload.ok) throw new GenerationError('Uploading the start image failed.');
      const uploaded = (await upload.json()) as { path: string; filename: string };

      params.image_start = uploaded.path;
      const ipt = String(params.image_prompt_type ?? '');
      params.image_prompt_type = ipt.includes('S') ? ipt : `S${ipt.replace(/T/g, '')}`;
    } else {
      // Text-only run: strip inherited S/V/E flags, otherwise wgp rejects the
      // job with "You must provide a Start Image".
      const ipt = String(params.image_prompt_type ?? '').replace(/[SVE]/g, '');
      params.image_prompt_type = ipt || 'T';
      delete params.image_start;
      delete params.image_end;
      delete params.video_source;
    }

    const submitted = await api<{ job_id: string }>('/api/v1/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
      signal,
    });
    const jobId = submitted.job_id;

    /* ── 2. POLL ────────────────────────────────────────────────────────────
     * Maestro reports 0-100; we normalise to 0-1 and extrapolate the remaining
     * time from elapsed. On cancel we also kill the job server-side rather
     * than just walking away from it. */
    const startedAt = performance.now();
    let last: MaestroJobStatus | null = null;
    /* The engine reports 100% when sampling ends, but then spends another
     * 60-200 s on VAE decoding and writing the file. Surfacing that raw would
     * park the bar and make the UI look frozen, so once the counter fills we
     * switch to a "finalizing" phase and creep 0.95 → 0.99 asymptotically. */
    let tailStartedAt = 0;

    const cancelOnServer = () => {
      // Fire-and-forget: the UI already treats the run as cancelled.
      void baseUrl()
        .then((b) =>
          fetch(`${b}/api/v1/cancel/${encodeURIComponent(jobId)}`, { method: 'POST' }),
        )
        .catch(() => undefined);
    };
    signal.addEventListener('abort', cancelOnServer, { once: true });
    // addEventListener does NOT fire on an already-aborted signal, so if the
    // abort landed between submit and subscribe, stop the job by hand —
    // otherwise it would keep running on the engine with nobody watching.
    if (signal.aborted) cancelOnServer();

    try {
      for (;;) {
        if (signal.aborted) throw abortError();

        const status = await api<MaestroJobStatus>(
          `/api/v1/status/${encodeURIComponent(jobId)}`,
        );
        last = status;

        const fraction = Math.max(0, Math.min(1, (status.progress || 0) / 100));
        const elapsed = (performance.now() - startedAt) / 1000;
        // Linear extrapolation — only show an ETA once there is something to
        // extrapolate from.
        const etaSeconds =
          fraction > 0.02 && fraction < 1 ? Math.max(0, elapsed / fraction - elapsed) : null;

        if (status.status === 'completed') {
          onProgress({
            stage: 'finalizing',
            progress: 0.97,
            etaSeconds: 0,
            message: 'Fetching file from the engine',
          });
          break;
        }

        if (status.status === 'failed') {
          throw new GenerationError(status.error || 'Maestro stopped with an error.');
        }
        if (status.status === 'cancelled') throw abortError();

        const label = status.phase || status.message || 'Generating';

        if (status.status === 'running' && fraction >= 1) {
          // ── Tail phase: the counter is full but the job is still working. ──
          if (!tailStartedAt) tailStartedAt = performance.now();
          const tailSeconds = (performance.now() - tailStartedAt) / 1000;
          // 0.95 → 0.99 with a ~40 s half-life. Never reaches 1 — only the
          // actual "completed" status is allowed to claim that.
          const crept = 0.95 + 0.04 * (1 - Math.exp(-tailSeconds / 40));
          onProgress({
            stage: 'finalizing',
            progress: crept,
            // Measured: decode + write runs roughly 70 s.
            etaSeconds: Math.max(0, 70 - tailSeconds),
            message: label,
            step: status.step,
            totalSteps: status.total_steps,
          });
        } else {
          onProgress({
            stage: status.status === 'queued' ? 'queued' : 'generating',
            progress: Math.max(0.03, fraction * 0.95),
            etaSeconds,
            message:
              status.total_steps > 0
                ? `${label} — step ${status.step}/${status.total_steps}`
                : label,
            step: status.step,
            totalSteps: status.total_steps,
          });
        }

        await sleep(POLL_INTERVAL_MS, signal);
      }
    } finally {
      signal.removeEventListener('abort', cancelOnServer);
    }

    /* ── 3. COLLECT THE RESULT ──────────────────────────────────────────────
     * The job returns filenames in output_files; the bytes come from
     * /api/v1/file/. We pull it down as a blob so the player and the download
     * button behave exactly as they do for the mock engine. */
    const files = last?.output_files ?? [];
    const videoName =
      files.find((f) => /\.(mp4|webm|mkv|gif)$/i.test(f)) ?? files[files.length - 1];

    if (!videoName) {
      throw new GenerationError('Maestro finished but returned no video file.');
    }

    const fileRes = await fetch(
      `${await baseUrl()}/api/v1/file/${encodeURIComponent(videoName)}`,
      { signal },
    );
    if (!fileRes.ok) {
      throw new GenerationError(`Could not download the finished file: ${videoName}`);
    }
    const blob = await fileRes.blob();
    const videoUrl = URL.createObjectURL(blob);

    const meta = await probeVideo(videoUrl);
    const [defW, defH] = String(params.resolution).split('x').map(Number);

    return {
      videoUrl,
      // Maestro returns no thumbnail, so we grabbed a frame ourselves in
      // probeVideo — that becomes the history thumbnail.
      posterUrl: meta.poster,
      mimeType: blob.type || 'video/mp4',
      filename: videoName.split(/[\\/]/).pop() || videoName,
      durationSeconds: meta.duration || 0,
      width: meta.width || defW || 0,
      height: meta.height || defH || 0,
    };
  },

  releaseResult(result: GenerationResult) {
    if (result.videoUrl.startsWith('blob:')) URL.revokeObjectURL(result.videoUrl);
  },

  /**
   * Maestro keeps every rendered clip on disk under its own output folder —
   * `filename` (persisted in history) is all that's needed to fetch it back,
   * even in a brand-new page session. Resolves `null` (never throws) when
   * the engine is unreachable or the file no longer exists, so the History
   * view can show a plain, honest "not available" instead of an error.
   */
  async resolveHistoryUrl(filename: string): Promise<string | null> {
    try {
      const res = await fetch(`${await baseUrl()}/api/v1/file/${encodeURIComponent(filename)}`);
      if (!res.ok) return null;
      const blob = await res.blob();
      return URL.createObjectURL(blob);
    } catch {
      return null;
    }
  },
};
